import { fetchJSON } from '../utils/fetchJSON';
import { trackNetworkRequest } from '../utils/performance';
import { storageService } from './storageService';

type AuthedFetchOptions = RequestInit & {
  cachePolicy?: 'default' | 'force-cache' | 'no-store';
  cacheTtlMs?: number;
};

type CachedResponseEntry = {
  data: unknown;
  expiresAt: number;
  authScope: string;
  invalidationPrefix: string;
};

class AbortRequestError extends Error {
  constructor(message = 'Request aborted') {
    super(message);
    this.name = 'AbortError';
  }
}

// Token cache to avoid reading localStorage on every API call
let cachedToken: string | null = null;
let cacheTimestamp = 0;
const CACHE_TTL_MS = 5000; // 5 seconds

// In-flight request deduplication - prevents duplicate concurrent requests
const inFlightRequests = new Map<string, Promise<any>>();
const responseCache = new Map<string, CachedResponseEntry>();
const RESPONSE_CACHE_MAX_ENTRIES = 120;

// Rate limit handling - exponential backoff for 429 errors
const rateLimitState = {
  consecutive429s: 0,
  last429At: 0,
  backoffUntil: 0
};

// Request budget monitoring
const requestBudget = {
  count: 0,
  windowStart: Date.now(),
  windowMs: 60000, // 1 minute window
  maxRequests: 100, // Warning threshold
  logThreshold: 0.8 // Warn at 80%
};

const getRequestKey = (url: string, method: string, body?: any) => {
  return `${method}:${url}:${body ? JSON.stringify(body) : ''}`;
};

const CACHEABLE_GET_TTLS: Array<{ pattern: RegExp; ttlMs: number }> = [
  { pattern: /\/api\/health-state(?:\/|$|\?)/, ttlMs: 8000 },
  { pattern: /\/api\/profile(?:\/|$|\?)/, ttlMs: 30000 },
  { pattern: /\/api\/labs(?:\/|$|\?)/, ttlMs: 30000 },
  { pattern: /\/api\/privacy\/consent(?:\/|$|\?)/, ttlMs: 45000 },
  { pattern: /\/api\/automation\/settings(?:\/|$|\?)/, ttlMs: 20000 },
  { pattern: /\/api\/protocols(?:\/library)?(?:\/|$|\?)/, ttlMs: 12000 },
  { pattern: /\/api\/supplements(?:\/|$|\?)/, ttlMs: 15000 },
  { pattern: /\/api\/habits(?:\/|$|\?)/, ttlMs: 15000 },
  { pattern: /\/api\/playbooks(?:\/|$|\?)/, ttlMs: 30000 },
  { pattern: /\/api\/referrals(?:\/|$|\?)/, ttlMs: 30000 },
  { pattern: /\/api\/integrations\/snapshot(?:\/|$|\?)/, ttlMs: 12000 }
];

const parseUrl = (url: string) => {
  const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  return new URL(url, base);
};

const getInvalidationPrefix = (pathname: string) => {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length >= 2) {
    return `/${parts[0]}/${parts[1]}`;
  }
  return pathname || '/';
};

const getDefaultCacheTtlMs = (url: string, method: string) => {
  if (method !== 'GET') return 0;
  const pathname = parseUrl(url).pathname;
  const match = CACHEABLE_GET_TTLS.find((entry) => entry.pattern.test(pathname));
  return match?.ttlMs ?? 0;
};

const cloneCachedData = <T>(data: T): T => {
  if (typeof structuredClone === 'function') {
    return structuredClone(data);
  }
  try {
    return JSON.parse(JSON.stringify(data)) as T;
  } catch {
    return data;
  }
};

const getResponseCacheKey = (requestKey: string, authScope: string) => `${authScope}:${requestKey}`;

const getCachedResponse = <T>(key: string) => {
  const entry = responseCache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    responseCache.delete(key);
    return null;
  }
  return cloneCachedData(entry.data as T);
};

const setCachedResponse = (key: string, authScope: string, url: string, data: unknown, ttlMs: number) => {
  const invalidationPrefix = getInvalidationPrefix(parseUrl(url).pathname);
  responseCache.set(key, {
    data,
    expiresAt: Date.now() + ttlMs,
    authScope,
    invalidationPrefix
  });

  while (responseCache.size > RESPONSE_CACHE_MAX_ENTRIES) {
    const oldestKey = responseCache.keys().next().value;
    if (!oldestKey) break;
    responseCache.delete(oldestKey);
  }
};

const invalidateCachedResponses = (url: string, authScope: string) => {
  const invalidationPrefix = getInvalidationPrefix(parseUrl(url).pathname);
  for (const [key, entry] of responseCache.entries()) {
    if (entry.authScope === authScope && entry.invalidationPrefix === invalidationPrefix) {
      responseCache.delete(key);
    }
  }
};

const dedupeRequest = <T>(key: string, requestFn: () => Promise<T>): Promise<T> => {
  if (inFlightRequests.has(key)) {
    return inFlightRequests.get(key) as Promise<T>;
  }
  const promise = requestFn().finally(() => {
    inFlightRequests.delete(key);
  });
  inFlightRequests.set(key, promise);
  return promise;
};

const isAbortError = (error: unknown) => (
  error instanceof AbortRequestError
  || (error instanceof DOMException && error.name === 'AbortError')
  || (error instanceof Error && error.name === 'AbortError')
);

const throwIfAborted = (signal?: AbortSignal | null) => {
  if (signal?.aborted) {
    throw new AbortRequestError();
  }
};

const waitWithAbort = (delayMs: number, signal?: AbortSignal | null) => new Promise<void>((resolve, reject) => {
  if (!delayMs) {
    resolve();
    return;
  }

  if (signal?.aborted) {
    reject(new AbortRequestError());
    return;
  }

  const timeoutId = globalThis.setTimeout(() => {
    cleanup();
    resolve();
  }, delayMs);

  const handleAbort = () => {
    cleanup();
    reject(new AbortRequestError());
  };

  const cleanup = () => {
    globalThis.clearTimeout(timeoutId);
    signal?.removeEventListener('abort', handleAbort);
  };

  signal?.addEventListener('abort', handleAbort, { once: true });
});

// Calculate exponential backoff delay (max 30 seconds)
const getBackoffDelay = (consecutive429s: number): number => {
  const baseDelay = 1000; // 1 second
  const maxDelay = 30000; // 30 seconds
  return Math.min(baseDelay * Math.pow(2, consecutive429s), maxDelay);
};

// Check and update request budget
const trackRequestBudget = () => {
  const now = Date.now();
  if (now - requestBudget.windowStart > requestBudget.windowMs) {
    // Reset window
    requestBudget.count = 0;
    requestBudget.windowStart = now;
  }
  requestBudget.count++;
  
  const usage = requestBudget.count / requestBudget.maxRequests;
  if (usage >= requestBudget.logThreshold && usage < 1) {
    console.warn(`[RequestBudget] ${Math.round(usage * 100)}% of warning threshold reached (${requestBudget.count}/${requestBudget.maxRequests} in last minute)`);
  } else if (usage >= 1 && requestBudget.count === Math.ceil(requestBudget.maxRequests)) {
    console.warn(`[RequestBudget] Exceeded warning threshold - rate limiting likely`);
  }
};

// Handle rate limit response
const handleRateLimit = (retryAfter?: number) => {
  const now = Date.now();
  rateLimitState.consecutive429s++;
  rateLimitState.last429At = now;
  
  const delay = retryAfter 
    ? retryAfter * 1000 
    : getBackoffDelay(rateLimitState.consecutive429s);
  
  rateLimitState.backoffUntil = now + delay;
  
  console.warn(`[RateLimit] Backing off for ${Math.round(delay / 1000)}s (consecutive 429s: ${rateLimitState.consecutive429s})`);
  
  return delay;
};

// Reset rate limit state on successful request
const resetRateLimitState = () => {
  if (rateLimitState.consecutive429s > 0) {
    console.log(`[RateLimit] Reset after ${rateLimitState.consecutive429s} consecutive 429s`);
    rateLimitState.consecutive429s = 0;
    rateLimitState.backoffUntil = 0;
  }
};

// Listen for storage changes to invalidate cache
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === 'longevityos:session') {
      cachedToken = null;
      cacheTimestamp = 0;
      responseCache.clear();
    }
  });
}

const buildHeaders = (options: RequestInit = {}, token?: string | null) => {
  const headers = new Headers(options.headers || undefined);

  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (token && !headers.has('X-Session-Token')) {
    headers.set('X-Session-Token', token);
  }

  return Object.fromEntries(headers.entries());
};

const readSessionToken = () => {
  const now = Date.now();
  
  // Return cached token if still valid
  if (cachedToken && (now - cacheTimestamp) < CACHE_TTL_MS) {
    return cachedToken;
  }
  
  try {
    const sessionStr = storageService.getItem('longevityos:session');
    if (sessionStr) {
      const session = JSON.parse(sessionStr);
      if (session && session.token) {
        cachedToken = session.token as string;
        cacheTimestamp = now;
        return cachedToken;
      }
    }
  } catch (e) {
    console.error('Error reading session for fetch', e);
  }
  
  cachedToken = null;
  cacheTimestamp = now;
  return null;
};

// Public method to clear cache (e.g., on logout)
export const clearSessionCache = () => {
  cachedToken = null;
  cacheTimestamp = 0;
  responseCache.clear();
};

export const apiService = {
  fetchJSON,

  async get(url: string, options: RequestInit = {}) {
    const completeRequest = trackNetworkRequest(url, 'GET');
    try {
      const res = await fetch(url, { ...options, method: 'GET' });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }
      return res;
    } finally {
      completeRequest();
    }
  },

  async post(url: string, body?: any, options: RequestInit = {}) {
    const completeRequest = trackNetworkRequest(url, 'POST');
    try {
      const res = await fetch(url, {
        ...options,
        method: 'POST',
        headers: buildHeaders(options),
        body: body === undefined ? undefined : JSON.stringify(body)
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }
      return res;
    } finally {
      completeRequest();
    }
  },

  async authedFetchJSON<T = any>(url: string, options: AuthedFetchOptions = {}, retryCount = 0): Promise<T> {
    const method = (options.method || 'GET').toUpperCase();
    const signal = options.signal;
    const completeRequest = trackNetworkRequest(url, method);
    throwIfAborted(signal);
    const token = readSessionToken();
    const authScope = token || 'anonymous';
    const key = getRequestKey(url, method, options.body);
    const cacheTtlMs = options.cachePolicy === 'no-store'
      ? 0
      : (options.cacheTtlMs ?? getDefaultCacheTtlMs(url, method));
    const responseCacheKey = getResponseCacheKey(key, authScope);

    if (method === 'GET' && cacheTtlMs > 0) {
      const cachedResponse = getCachedResponse<T>(responseCacheKey);
      if (cachedResponse !== null) {
        return cachedResponse;
      }
    }

    // Check if we're in backoff period
    const now = Date.now();
    if (now < rateLimitState.backoffUntil) {
      const waitMs = rateLimitState.backoffUntil - now;
      console.warn(`[RateLimit] Waiting ${Math.round(waitMs / 1000)}s before request due to backoff`);
      await waitWithAbort(waitMs, signal);
    }

    trackRequestBudget();

    try {
      return await dedupeRequest<T>(key, async (): Promise<T> => {
        throwIfAborted(signal);
        const headers = buildHeaders(options, token);
        let res: Response;
        try {
          res = await fetch(url, { ...options, headers, signal });
        } catch (error) {
          if (isAbortError(error)) {
            throw new AbortRequestError();
          }
          throw error;
        }

        // Handle 429 with exponential backoff
        if (res.status === 429) {
          const retryAfterHeader = res.headers.get('Retry-After');
          const retryAfter = retryAfterHeader ? parseInt(retryAfterHeader, 10) : undefined;
          const delay = handleRateLimit(retryAfter);

          // Retry up to 3 times
          if (retryCount < 3) {
            console.warn(`[RateLimit] Retrying after ${Math.round(delay / 1000)}s (attempt ${retryCount + 1}/3)`);
            await waitWithAbort(delay, signal);
            return this.authedFetchJSON<T>(url, options, retryCount + 1);
          }

          throw new Error(`Rate limited: Too many requests. Retry after ${Math.round(delay / 1000)}s`);
        }

        // Reset rate limit state on success
        resetRateLimitState();

        if (!res.ok) {
          const txt = await res.text();
          try {
            const json = JSON.parse(txt);
            throw new Error(json.error || json.message || `HTTP ${res.status}`);
          } catch {
            throw new Error(txt || `HTTP ${res.status}`);
          }
        }
        const contentType = res.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
          const txt = await res.text();
          const looksLikeHtml = /^\s*<!doctype html>|^\s*<html/i.test(txt);
          if (looksLikeHtml) {
            throw new Error(`API routing misconfigured for ${url}: received HTML instead of JSON`);
          }
          throw new Error(txt || `Unexpected response ${res.status}`);
        }
        const json = await res.json() as T;
        if (method === 'GET' && cacheTtlMs > 0) {
          setCachedResponse(responseCacheKey, authScope, url, json, cacheTtlMs);
        } else if (method !== 'GET') {
          invalidateCachedResponses(url, authScope);
        }
        return json;
      });
    } finally {
      completeRequest();
    }
  },

  async authedGet(url: string, options: RequestInit = {}) {
    const completeRequest = trackNetworkRequest(url, 'GET');
    try {
      const token = readSessionToken();
      const headers = buildHeaders(options, token);
      const res = await fetch(url, { ...options, method: 'GET', headers });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }
      return res;
    } finally {
      completeRequest();
    }
  },

  async authedPost(url: string, body?: any, options: RequestInit = {}) {
    const completeRequest = trackNetworkRequest(url, 'POST');
    try {
      const token = readSessionToken();
      const headers = buildHeaders(options, token);
      const res = await fetch(url, {
        ...options,
        method: 'POST',
        headers,
        body: body === undefined ? undefined : JSON.stringify(body)
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }
      return res;
    } finally {
      completeRequest();
    }
  },

  async put(url: string, body?: any, options: RequestInit = {}) {
    const completeRequest = trackNetworkRequest(url, 'PUT');
    try {
      const res = await fetch(url, {
        ...options,
        method: 'PUT',
        headers: buildHeaders(options),
        body: body === undefined ? undefined : JSON.stringify(body)
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }
      return res;
    } finally {
      completeRequest();
    }
  },

  async delete(url: string, options: RequestInit = {}) {
    const completeRequest = trackNetworkRequest(url, 'DELETE');
    try {
      const res = await fetch(url, {
        ...options,
        method: 'DELETE',
        headers: buildHeaders(options)
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }
      return res;
    } finally {
      completeRequest();
    }
  }
};
